import { useCallback, useEffect, useRef, useState } from 'react'
import { agents } from '../../../../../../api/agents'
import { getAgentConfig } from '../agentConfigs'
import { extractTextContent, stripAttachmentBlocks } from '../message/messageUtils'

function getInFlightToolMessage (messages, toolMessages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role !== 'assistant' || !Array.isArray(message.toolCalls) || message.toolCalls.length === 0) {
      continue
    }

    for (const toolCall of message.toolCalls.slice().reverse()) {
      const hasToolResult = messages.slice(i + 1).some(nextMessage => (
        nextMessage?.role === 'tool' && (
          (toolCall.id && nextMessage.toolCallId === toolCall.id) ||
          (toolCall.name && nextMessage.name === toolCall.name)
        )
      ))

      if (!hasToolResult) {
        return toolMessages[toolCall.name] || `Running ${toolCall.name}...`
      }
    }
  }

  return null
}

/**
 * Generic agent panel hook. Manages thread state, message sending,
 * and agent status for any configured agent.
 *
 * @param {string} agentId - Agent identifier (e.g. 'ae-view-builder')
 * @param {Object} [options]
 * @param {string} [options.initialThreadId] - Resume an existing thread
 * @param {Object} [options.viewContext] - View context for attachment building ({ viewId, content })
 */
export function useAgentPanel (agentId, options = {}) {
  const { initialThreadId, viewContext } = options
  const config = getAgentConfig(agentId)

  const [threadId, setThreadId] = useState(initialThreadId || null)
  const [pendingSend, setPendingSend] = useState(false)
  const [optimisticMessage, setOptimisticMessage] = useState(null)
  const messageCountAtSend = useRef(null)

  const { mutateAsync: invokeAgent, isError: invokeError, isLoading: isInvoking } = agents.useInvokeAgent(config.agentId)
  const { data: threadData, isLoading: isThreadLoading, isFetching: isThreadFetching } = agents.useAgentThread(threadId, {
    enabled: !!threadId,
    agentId: config.agentId
  })

  const status = threadData?.status
  const serverMessages = threadData?.messages || []

  // Clear pendingSend once the thread acknowledges processing
  useEffect(() => {
    if (!pendingSend) return

    const threadIsProcessing = status === 'pending' || status === 'running'
    const messagesIncreased = messageCountAtSend.current !== null && serverMessages.length > messageCountAtSend.current

    if (threadIsProcessing || messagesIncreased) {
      setPendingSend(false)
      messageCountAtSend.current = null
    }
  }, [pendingSend, status, serverMessages.length])

  // Clear optimistic message only when the server messages actually include it
  const serverHasOptimistic = optimisticMessage && serverMessages.some(m => {
    if (m.role !== 'human' && m.role !== 'user') return false
    const serverText = stripAttachmentBlocks(extractTextContent(m.content))
    return serverText === optimisticMessage.content
  })

  useEffect(() => {
    if (serverHasOptimistic) {
      setOptimisticMessage(null)
    }
  }, [serverHasOptimistic])

  const messages = optimisticMessage && !serverHasOptimistic
    ? [...serverMessages, optimisticMessage]
    : serverMessages

  const addMessage = useCallback(async (message) => {
    const attachments = config.buildAttachments(viewContext)

    messageCountAtSend.current = serverMessages.length
    setPendingSend(true)
    setOptimisticMessage({ role: 'human', content: message })

    try {
      const result = await invokeAgent({
        message,
        threadId: threadId || undefined,
        ...(attachments.length > 0 ? { attachments } : {})
      })

      if (!threadId && result?.threadId) {
        setThreadId(result.threadId)
      }

      return result
    } catch (error) {
      setPendingSend(false)
      setOptimisticMessage(null)
      throw error
    }
  }, [invokeAgent, threadId, viewContext, config, serverMessages.length])

  const isProcessing = status === 'pending' || status === 'running' || isInvoking || pendingSend
  const hasMessages = messages.length > 0
  const isLoadingState = isInvoking || pendingSend || (!hasMessages && threadId && (isThreadLoading || isThreadFetching))

  const processingMessage =
    getInFlightToolMessage(messages, config.toolMessages) ||
    (status === 'pending' ? 'Queued request...' : null) ||
    (isProcessing ? 'Thinking...' : null)

  return {
    addMessage,
    threadId,
    messages,
    agentStatus: isProcessing ? 'processing' : (status || 'idle'),
    processingMessage,
    isThreadLoading: isLoadingState,
    isErrored: invokeError || status === 'error',
    config,
    viewContext
  }
}
